import React from 'react';
import { IMFormModal, IMSelect, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import SearchBlock, { SearchInfo } from 'components/common/general/SearchBlock';
import SubtitleWithLines from 'components/common/general/SubtitleWithLines';
import { ModalType } from 'global/utils/ModalsHelper';
import { observer } from 'mobx-react-lite';
import { useBiDashboardPageStore } from 'store/pages/bi-groups-dashboard/pageStore';
import { MOVE_GROUP_MODAL_ID } from 'components/bi-dashboard/constants';
import {
  CurrentAffiliationBlockStyled,
  FormWrapperStyled,
  DisabledSelectStyled,
  StyledGroupName,
  SubtitleWithLinesStyled,
  StepBlockStyled,
  StepHeaderStyled,
  StepNumberStyled,
  StepHeaderTextStyled,
  TargetAgencyBlockStyled,
  IMSelectStyled,
  AorBlockStyled,
  NoAorsAvailableMessage,
} from './styles';

const MoveGroupModal: React.FC = observer(() => {
  const {
    groupInteractionDelegate: {
      moveModal,
      selectedGroup,
      moveGroupModalUiStore: {
        searchAgencyDelegate,
        form,
        isAorSectionLoading,
        agencyAors,
        resetModalData,
        setAgencyId,
      },
    },
  } = useBiDashboardPageStore();

  const agencyOptions: OptionType[] = searchAgencyDelegate.allSponsorAgencies.map((sponsorAgency) => ({
    label: sponsorAgency.name,
    value: sponsorAgency.id,
  }));

  const onSetAgency = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setAgencyId(e.target.value);
  };

  const selectAgencyFromSearch = (id: string): void => {
    setAgencyId(id);
    searchAgencyDelegate.resetSearchValue();
  };

  const onClose = (): void => {
    resetModalData();
    moveModal.close();
  };

  return (
    <IMFormModal
      show={moveModal.isVisible}
      title="Move Group"
      subtitle={
        <>
          You are about to move <StyledGroupName>{selectedGroup.name}</StyledGroupName> group.
          <br /> Please choose the target agency from the list below.
        </>
      }
      closeButtonText="Cancel"
      submitButtonText="Move group"
      onClose={onClose}
      modalType={ModalType.NOTIFICATION}
      formController={form}
      formId={MOVE_GROUP_MODAL_ID}
      formContent={
        <FormWrapperStyled>
          <SubtitleWithLines text="Current Group Affiliation" />
          <CurrentAffiliationBlockStyled>
            <DisabledSelectStyled
              name="currentAgency"
              label="Current agency"
              options={agencyOptions}
              value={selectedGroup.agencyId}
              placeholder="Select agency"
              disabled
              width="100%"
            />
            <DisabledSelectStyled
              name="currentAor"
              label="Agent of Record"
              options={[]}
              value=""
              placeholder={selectedGroup.aor?.name || 'Agent of Record is Missing'}
              disabled
              width="100%"
            />
          </CurrentAffiliationBlockStyled>

          <SubtitleWithLinesStyled text="Updated Group Affiliation" />
          <StepBlockStyled>
            <StepHeaderStyled>
              <StepNumberStyled>1</StepNumberStyled>
              <StepHeaderTextStyled>Select Target Agency</StepHeaderTextStyled>
            </StepHeaderStyled>
            <TargetAgencyBlockStyled>
              <IMSelectStyled
                name="agencyId"
                label="Target agency"
                options={agencyOptions}
                onChange={onSetAgency}
                placeholder="Select agency"
              />
              <SearchBlock
                inputLabel="Search agency"
                placeholder="Agency name"
                filteredInfo={searchAgencyDelegate.filteredSponsorAgencies.map(({ name, id }) => ({ name, id }))}
                resetFilteredItems={searchAgencyDelegate.resetSearchValue}
                searchByValue={(value: string): void => searchAgencyDelegate.searchAgencyByName(value)}
                selectFromSearch={(info: SearchInfo): void => selectAgencyFromSearch(info.id)}
              />
            </TargetAgencyBlockStyled>
          </StepBlockStyled>

          <StepBlockStyled>
            <OverlaySpinner display={isAorSectionLoading} size="5rem" />
            <StepHeaderStyled>
              <StepNumberStyled>2</StepNumberStyled>
              <StepHeaderTextStyled>Select Agent of Record</StepHeaderTextStyled>
            </StepHeaderStyled>
            {agencyAors.length < 1 ? (
              <NoAorsAvailableMessage>
                There are currently no available Agent of Records for this agency.
              </NoAorsAvailableMessage>
            ) : (
              <AorBlockStyled>
                <IMSelect
                  name="aorId"
                  label="Agent of Record"
                  options={agencyAors.map((aor) => ({ label: aor.name, value: aor.id }))}
                  placeholder="Select agent of record"
                  $size="full"
                />
              </AorBlockStyled>
            )}
          </StepBlockStyled>
        </FormWrapperStyled>
      }
    />
  );
});

export default MoveGroupModal;
